import { DATA_SET_PAGINATION_COUNT, DATA_SET_PAGINATION_COUNT_TASK_ACTIVITIES } from 'constants/dataSets';
import { API_METHODS, SHEET_EXAMPLE_FILES_NAME } from 'constants/general';
import {
  COMPANY_ADMIN_USERS_INFO,
  TASK_ACTIVITIES,
  TASK_ACTIVITIES_COMMENT,
  TASK_ASSIGNED_USERS,
  TASK_ATTACHMENTS, TASK_DELETE_ATTACHMENT, TASK_EXPORT, TASK_MANAGEMENT, TASK_MANAGEMENT_LIST,
} from 'constants/requestUrls';
import mainApi from 'store/apis/mainApi';
import TASK_MANAGEMENT_LIST_TAG, { TASK_MANAGEMENT_ACTIVITIES_TAG, TASK_MANAGEMENT_ATTACHMENTS_TAG, TASK_MANAGEMENT_RECORD_TAG } from 'store/tags/tasksManagement';
import downloadFileHandler from 'utils/downloadFileHandler';

import type { IBaseQueryPaginationResponse } from 'store/types/IBaseQueryPaginationResponse';
import type IBaseQueryResponse from 'store/types/IBaseQueryResponse';
import type ICommentTask from '../interfaces/ICommentTask';
import type ICreateFileAttachmentRequest from '../interfaces/ICreateFileAttachmentRequest';
import type IDeleteFileAttachmentRequest from '../interfaces/IDeleteFileAttachmentRequest';
import type IDeleteTaskRequest from '../interfaces/IDeleteTaskRequest';
import type IIsignUsersInfoListRequest from '../interfaces/IIsignUsersInfoListRequest';
import type IIssignUsersInfo from '../interfaces/IIssignUsersInfo';
import type ISingleTask from '../interfaces/ISingleTask';
import type ISingleTaskRequest from '../interfaces/ISingleTaskRequest';
import type ITask from '../interfaces/ITask';
import type ITaskAttachments from '../interfaces/ITaskAttachments';
import type ITaskAttachmentsRequest from '../interfaces/ITaskAttachmentsRequest';
import type ITaskCommentCreateRequest from '../interfaces/ITaskCommentCreateRequest';
import type ITaskCommentDeleteRequest from '../interfaces/ITaskCommentDeleteRequest';
import type ITaskCommentListRequest from '../interfaces/ITaskCommentListRequest';
import type ITaskCreateRequest from '../interfaces/ITaskCreateRequest';
import type ITaskCreateResponse from '../interfaces/ITaskCreateResponse';
import type ITaskExportRequest from '../interfaces/ITaskExportRequest';
import type ITaskListRequest from '../interfaces/ITaskListRequest';
import type ITaskUpdateRequest from '../interfaces/ITaskUpdateRequest';
import type ITaskUpdateSignToRequest from '../interfaces/ITaskUpdateSignToRequest';
import type ITaskUpdateStatusRequest from '../interfaces/ITaskUpdateStatusRequest';

export const tasksManagementApi = mainApi.injectEndpoints({
  endpoints: (build) => ({
    getTasks: build.query<IBaseQueryPaginationResponse<ITask[]>, ITaskListRequest>({
      query: ({
        offset, orderBy, keyword, statuses, valueChainMapIds, materialityAssessmentIds,
      }) => ({
        url: TASK_MANAGEMENT_LIST,
        params: {
          ...(orderBy && { orderBy }),
          ...(keyword && { keyword }),
          ...(offset ? { offset } : { offset: 0 }),
          ...(statuses && { statuses }),
          ...(valueChainMapIds && { valueChainMapIds }),
          ...(materialityAssessmentIds && { materialityAssessmentIds }),
          limit: DATA_SET_PAGINATION_COUNT,
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: TASK_MANAGEMENT_LIST_TAG, id }))]
          : [TASK_MANAGEMENT_LIST_TAG]
      ),
    }),
    getCommentsTasks: build.query<IBaseQueryPaginationResponse<ICommentTask[]>, ITaskCommentListRequest>({
      query: ({
        offset, taskId, category,
      }) => ({
        url: TASK_ACTIVITIES,
        params: {
          ...(offset ? { offset } : { offset: 0 }),
          ...(taskId && { taskId }),
          ...(category && { category }),
          limit: DATA_SET_PAGINATION_COUNT_TASK_ACTIVITIES,
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: TASK_MANAGEMENT_ACTIVITIES_TAG, id }))]
          : [TASK_MANAGEMENT_ACTIVITIES_TAG]
      ),
    }),
    getSingleTask: build.query<IBaseQueryResponse<ISingleTask>, ISingleTaskRequest>({
      query: ({ id }) => ({
        url: TASK_MANAGEMENT.replace('{id}', `${id}`),
      }),
      providesTags: (result) => (
        result?.data?.id
          ? [{ type: TASK_MANAGEMENT_RECORD_TAG, id: result.data?.id }]
          : [TASK_MANAGEMENT_RECORD_TAG]
      ),
    }),
    createTask: build.mutation<IBaseQueryResponse<ITaskCreateResponse>,
    ITaskCreateRequest>({
      query: (data) => ({
        url: TASK_MANAGEMENT_LIST,
        method: API_METHODS.post,
        body: data,
      }),
      invalidatesTags: [TASK_MANAGEMENT_LIST_TAG, TASK_MANAGEMENT_ATTACHMENTS_TAG],
    }),
    updateStatusTask: build.mutation<IBaseQueryResponse<ITaskCreateResponse>,
    ITaskUpdateStatusRequest>({
      query: ({
        id, status, type, materialityAssessmentId, valueChainMapId, updateComment,
      }) => ({
        url: TASK_MANAGEMENT.replace('{id}', `${id}`),
        method: API_METHODS.patch,
        body: {
          status, type, materialityAssessmentId, valueChainMapId, updateComment,
        },
      }),
      invalidatesTags: [TASK_MANAGEMENT_LIST_TAG, TASK_MANAGEMENT_RECORD_TAG,
        TASK_MANAGEMENT_ACTIVITIES_TAG, TASK_MANAGEMENT_ATTACHMENTS_TAG],
    }),
    updateTask: build.mutation<IBaseQueryResponse<ITaskCreateResponse>,
    ITaskUpdateRequest>({
      query: ({ data, id }) => ({
        url: TASK_MANAGEMENT.replace('{id}', `${id}`),
        method: API_METHODS.patch,
        body: data,
      }),
      invalidatesTags: [TASK_MANAGEMENT_LIST_TAG, TASK_MANAGEMENT_RECORD_TAG, TASK_MANAGEMENT_ATTACHMENTS_TAG],
    }),
    deleteTask: build.mutation<void, IDeleteTaskRequest>({
      query: ({ id }) => ({
        url: TASK_MANAGEMENT.replace('{id}', `${id}`),
        method: API_METHODS.delete,
      }),
      invalidatesTags: [TASK_MANAGEMENT_LIST_TAG],
    }),
    updateSignToTask: build.mutation<void, ITaskUpdateSignToRequest>({
      query: ({ id, userIds }) => ({
        url: TASK_ASSIGNED_USERS.replace('{id}', `${id}`),
        method: API_METHODS.patch,
        body: { userIds },
      }),
      invalidatesTags: [TASK_MANAGEMENT_LIST_TAG, TASK_MANAGEMENT_RECORD_TAG,
        TASK_MANAGEMENT_ACTIVITIES_TAG, TASK_MANAGEMENT_ATTACHMENTS_TAG],
    }),
    getAttachments: build.query<IBaseQueryPaginationResponse<ITaskAttachments[]>, ITaskAttachmentsRequest>({
      query: ({
        offset, taskId,
      }) => ({
        url: TASK_ATTACHMENTS,
        params: {
          ...(offset ? { offset } : { offset: 0 }),
          ...(taskId && { taskId }),
          limit: DATA_SET_PAGINATION_COUNT,
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: TASK_MANAGEMENT_ATTACHMENTS_TAG as typeof TASK_MANAGEMENT_ATTACHMENTS_TAG, id }))]
          : [TASK_MANAGEMENT_ATTACHMENTS_TAG]
      ),
    }),
    createFileAttachment: build.mutation<void, ICreateFileAttachmentRequest>({
      query: ({ attachments, taskId }) => ({
        url: TASK_ATTACHMENTS,
        method: API_METHODS.post,
        body: {
          attachments,
          ...(taskId && { taskId }),
        },
      }),
      invalidatesTags: [TASK_MANAGEMENT_LIST_TAG, TASK_MANAGEMENT_ATTACHMENTS_TAG, TASK_MANAGEMENT_ACTIVITIES_TAG],
    }),
    deleteFileAttachment: build.mutation<void, IDeleteFileAttachmentRequest>({
      query: ({ id }) => ({
        url: TASK_DELETE_ATTACHMENT.replace('{id}', `${id}`),
        method: API_METHODS.delete,
      }),
      invalidatesTags: [TASK_MANAGEMENT_LIST_TAG, TASK_MANAGEMENT_ACTIVITIES_TAG, TASK_MANAGEMENT_ATTACHMENTS_TAG],
    }),
    createCommentTask: build.mutation<void, ITaskCommentCreateRequest>({
      query: (data) => ({
        url: TASK_ACTIVITIES_COMMENT,
        method: API_METHODS.post,
        body: data,
      }),
      invalidatesTags: [TASK_MANAGEMENT_ACTIVITIES_TAG],
    }),
    deleteCommentTask: build.mutation<void, ITaskCommentDeleteRequest>({
      query: (data) => ({
        url: TASK_ACTIVITIES_COMMENT,
        method: API_METHODS.delete,
        body: data,
      }),
      invalidatesTags: [TASK_MANAGEMENT_ACTIVITIES_TAG],
    }),
    exportTasks: build.query<IBaseQueryPaginationResponse<void>, ITaskExportRequest>({
      query: ({
        orderBy, keyword, offset, statuses, valueChainMapIds,
      }) => ({
        url: TASK_EXPORT,
        params: {
          ...(orderBy && { orderBy }),
          ...(keyword && { keyword }),
          ...(offset ? { offset } : { offset: 0 }),
          ...(statuses && { statuses }),
          ...(valueChainMapIds && { valueChainMapIds }),
          limit: DATA_SET_PAGINATION_COUNT,
        },
        responseHandler: async (response) => downloadFileHandler({
          filename: SHEET_EXAMPLE_FILES_NAME.tasks,
          response,
        }),
        cache: 'no-cache',
      }),
    }),
    getIssignUsersInfo: build.query<IIssignUsersInfo, IIsignUsersInfoListRequest>({
      query: ({
        userIds,
      }) => ({
        url: COMPANY_ADMIN_USERS_INFO,
        params: {
          userIds,
        },
      }),
    }),
  }),
});

export const {
  useGetTasksQuery,
  useGetSingleTaskQuery,
  useCreateTaskMutation,
  useUpdateStatusTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useGetAttachmentsQuery,
  useDeleteFileAttachmentMutation,
  useUpdateSignToTaskMutation,
  useCreateFileAttachmentMutation,
  useGetCommentsTasksQuery,
  useCreateCommentTaskMutation,
  useDeleteCommentTaskMutation,
  useExportTasksQuery,
  useLazyExportTasksQuery,
  useGetIssignUsersInfoQuery,
  useLazyGetIssignUsersInfoQuery,
} = tasksManagementApi;
